/**
 * Static mock data for the screen gallery.
 *
 * Every screen renders fully without any API. This module is the single source
 * of truth for the fake session / cards / transactions / quote that get seeded
 * into the global stores on boot.
 */
import type { IWidgetSession } from '../shared/session/interfaces/session';
import type { ICardState } from '../interfaces/card';
import type { IPaymentSelected } from '../interfaces/payment';
import { paymentType } from '../interfaces/payment';
import type { ITransaction } from '../interfaces/transactions';
import { TransactionsStatus } from '../utils/commons';
import type { IQuoteResponse, IExchangeRateResponse } from '../services/api';
import { getMockTerms } from './mockTerms';

const ONE_HOUR_MS = 60 * 60 * 1000;

export const MOCK_LINK_ID = 'gallery-demo-link';

export const MOCK_EXCHANGE_RATE = 17.42;

export const mockAmount = '400';

/** Fund-request Transfer: default send amount (USD) the claim suggests. */
export const FUND_REQUEST_MOCK_SEND_AMOUNT = 50;

export const mockSession: IWidgetSession = {
  sessionId: 'gallery-session',
  linkId: MOCK_LINK_ID,
  token: 'gallery-token',
  status: 'active',
  expiresAt: new Date(Date.now() + 24 * ONE_HOUR_MS).toISOString(),
  partner: {
    partnerId: 'partner-demo',
    name: 'Kapital',
    // No real partner in the gallery: leaving the config logo unset makes every
    // config-driven logo slot render the dashed `LogoPlaceholder` instead of an
    // unrelated image. (Brand/asset logos like cards/stores are unaffected.)
    logoUrl: undefined,
    supportEmail: 'support@example.com',
    supportWhatsappNumber: '+14155550123',
    primaryColor: '#3D2BFF',
    theme: undefined,
    sessionExpiredRedirectUrl: 'https://example.com/return',
  },
  userData: {
    referenceId: 'user-demo-123',
    fullName: 'Jane Doe',
    name: { firstName: 'Jane', lastName: 'Doe' },
    paymentMethod: {
      type: 'bank_account',
      payoutMethod: 'BANK_ACCOUNT',
      bankAccount: {
        bankCode: 'BBVA',
        bankName: 'BBVA Mexico',
        accountNumber: '012180000000001234',
      },
    },
  },
  config: {
    sourceCountry: 'US',
    destinationCountry: 'MX',
    allowedFundingMethods: ['card', 'bank_account', 'cash'],
    allowedPayoutMethods: ['bank_account'],
    flow: 'm2m',
    // --- Request flow (optional, additive — ignored by the other flows) ---
    sourceCurrency: 'USD',
    destinationCurrency: 'MXN',
    defaultCorridor: { originCountryCode: 'US', destinationCountryCode: 'MX' },
    corridors: [
      {
        originCountryCode: 'US',
        destinationCountryCode: 'MX',
        sourceCurrencyCodes: ['USD'],
        destinationCurrencyCodes: ['MXN'],
      },
      {
        originCountryCode: 'US',
        destinationCountryCode: 'CO',
        destinationCurrencyCodes: ['COP'],
      },
      {
        originCountryCode: 'US',
        destinationCountryCode: 'AR',
        destinationCurrencyCodes: ['ARS'],
      },
      {
        originCountryCode: 'MX',
        destinationCountryCode: 'CO',
        destinationCurrencyCodes: ['COP'],
      },
    ],
    callbackUrl: 'https://example.com/return',
    returnToChatUrl: 'https://example.com/whatsapp-return',
    usage: { state: 'consumed', operationId: 'op_gallery_demo_001' },
    allowAmountEdit: false,
    allowPayoutEdit: false,
    allowDestinationCurrencyEdit: false,
    // Fund-request: suggested receive amount ($50 USD at gallery mock FX).
    amount: Number(
      (FUND_REQUEST_MOCK_SEND_AMOUNT * MOCK_EXCHANGE_RATE).toFixed(2),
    ),
  },
  // Partner-configured Terms & Conditions used by the Welcome consent screen.
  // Localized copy lives in `mockTerms.ts`; `useGalleryTerms` keeps this in
  // sync with the Control Dock language (en / es / fr).
  terms: getMockTerms('en'),
};

export const mockCards: ICardState[] = [
  {
    cardId: 'card_visa_demo',
    name: 'VISA-4242',
    brand: 'VISA',
    last4: '4242',
    accountNumber: null,
    expirationDate: '12/27',
    fullName: 'Jane Doe',
    cvv: null,
  },
  {
    cardId: 'card_mc_demo',
    name: 'MASTERCARD-5555',
    brand: 'MASTERCARD',
    last4: '5555',
    accountNumber: null,
    expirationDate: '08/26',
    fullName: 'Jane Doe',
    cvv: null,
  },
];

export const mockSelectedPayment: IPaymentSelected = {
  cardId: 'card_visa_demo',
  type: paymentType.CARD,
  brand: 'VISA-4242',
};

export const mockTransactions: ITransaction[] = [
  {
    status: TransactionsStatus.COMPLETED,
    amount: '6,968.00',
    currency: 'MXN',
    date: '08/06/26',
  },
  {
    status: TransactionsStatus.PENDING,
    amount: '3,484.00',
    currency: 'MXN',
    date: '05/06/26',
  },
  {
    status: TransactionsStatus.CANCELED,
    amount: '1,742.00',
    currency: 'MXN',
    date: '29/05/26',
  },
];

export const buildMockQuote = (
  amount = Number(mockAmount),
): IQuoteResponse => {
  const fee = 1;
  const receiveAmount = Number((amount * MOCK_EXCHANGE_RATE).toFixed(2));
  return {
    quoteId: 'quote-demo',
    sendAmount: amount,
    receiveAmount,
    exchangeRate: MOCK_EXCHANGE_RATE,
    fee,
    totalCost: Number((amount + fee).toFixed(2)),
    sourceCurrency: 'USD',
    destinationCurrency: 'MXN',
    fundingMethod: 'card',
    payoutMethod: 'bank_account',
    // Far-future expiry so the live-quote timer never fires in the gallery.
    expiresAt: new Date(Date.now() + 24 * ONE_HOUR_MS).toISOString(),
    signedQuote: 'gallery.signed.quote',
    cardId: mockSelectedPayment.cardId ?? undefined,
  };
};

export const mockQuote: IQuoteResponse = buildMockQuote();

export const buildMockExchangeRate = (
  sourceCurrency: string,
  destinationCurrency: string,
): IExchangeRateResponse => ({
  sourceCurrency,
  destinationCurrency,
  rate: MOCK_EXCHANGE_RATE,
  timestamp: new Date().toISOString(),
});
